import { RouterLocation } from '@funtech-int/rival';
import { useMemo } from 'react';

import { yasminaRoute } from '@/scenarios/yasmina/routes';
import { personalRoute, historyRoute } from '@/scenarios/personal/routes';
import { mainRoute } from '@/scenarios/home/routes';
import { filmRoute } from '@/scenarios/movie/routes';
import { crunchyrollRoute } from '@/scenarios/crunchyroll/routes';
import { channelsRoute } from '@/scenarios/channels/routes';
import { adminFamilyAddRoute, profilesRoute } from '@/scenarios/profiles/routes';
import { playerRoute } from '@/scenarios/player/routes';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { clientConfig } from '@/config/client';

import { HeaderType } from '../types/layout';

import { useRestrictedForNotification } from './useRestrictedForNotification';

const { isHeaderOfferButtonEnabled } = clientConfig;

interface LayoutProps {
    withNotifications: boolean;
    headerType: HeaderType;
    shouldShowHeader: boolean;
    shouldShowFooter: boolean;
    shouldShowSubscribeButton: boolean;
    isFluid: boolean;
}

function getHeaderType(location: RouterLocation<any, any>, hasSubscription: boolean): HeaderType {
    if (
        (mainRoute.is(location) && !hasSubscription) ||
        [yasminaRoute, crunchyrollRoute, filmRoute].some((route) => route.is(location))
    ) {
        return 'onboarding';
    }

    if ([adminFamilyAddRoute, profilesRoute].some((route) => route.is(location))) {
        return 'only-logo';
    }

    return 'base';
}

export function useLayoutProps(location: RouterLocation<any, any>): LayoutProps {
    const hasSubscription = useHasSubscription();
    const isRestrictedForNotification = useRestrictedForNotification();

    return useMemo(() => {
        const withNotifications =
            !isRestrictedForNotification &&
            [
                yasminaRoute,
                mainRoute,
                filmRoute,
                personalRoute,
                historyRoute,
                crunchyrollRoute,
                channelsRoute,
            ].some((route) => route.is(location));

        const shouldShowSubscribeButton =
            isHeaderOfferButtonEnabled && ![crunchyrollRoute].some((route) => route.is(location));

        const shouldShowHeader = ![playerRoute].some((route) => route.is(location));
        const shouldShowFooter = ![profilesRoute, adminFamilyAddRoute, playerRoute].some((route) =>
            route.is(location)
        );
        const isFluid = [playerRoute].some((route) => route.is(location));

        return {
            withNotifications,
            headerType: getHeaderType(location, hasSubscription),
            shouldShowHeader,
            shouldShowSubscribeButton,
            shouldShowFooter,
            isFluid,
        };
    }, [isRestrictedForNotification, location, hasSubscription]);
}
